/* CORNERS ACROSS A RE-CROP.

   S.corners is normalised to the photo — 0..1 of whatever image is currently
   loaded, not of the original upload. That is the right storage for everything
   else (it survives a window resize, a zoom, a different display), but it means
   the numbers describe a position in a FRAME rather than a position on a
   BUILDING. Re-crop the photo and the frame changes underneath them, so the
   same four pairs now point somewhere else entirely and the crosshairs appear
   to jump off the screen corners they were carefully placed on.

   The fix is to treat the original upload as the shared coordinate space. If we
   know the rect that produced the old photo from it, and the rect producing the
   new one, a corner can be carried across: forward through the old rect into
   original coordinates, back through the new one. Both rects are normalised to
   that same original, so the units cancel and no pixel dimensions are needed.

   Rotation is deliberately NOT handled — see cropPhoto's `rotated` flag. A
   rotate or a straighten bakes new content into the working image, at which
   point a rect is no longer a complete description of how the two frames
   relate, and a remap built from one would be confidently wrong. The caller
   passes null in that case and keeps today's behaviour: leave the numbers be
   and say so out loud, which is honest where a silent bad guess is not.

   No DOM, no S, no imports beyond clamp — the same contract core/video-time.js
   keeps, and for the same reason: this is the part worth testing directly. */
import { clamp } from './util.js';

const KEYS = ['tl', 'tr', 'br', 'bl'];

const usable = r => !!r && (r.x1 - r.x0) > 1e-9 && (r.y1 - r.y0) > 1e-9;

/**
 * Carry marked corners from one crop of an image to another.
 *
 * @param {object} corners - {tl,tr,br,bl}, each {x,y} normalised 0..1 to the
 *                           image the marks were made on.
 * @param {{x0:number,y0:number,x1:number,y1:number}} from - the rect that
 *        produced that image from the original, normalised to the original.
 * @param {{x0:number,y0:number,x1:number,y1:number}} to - the rect producing
 *        the new image, in the same space.
 * @returns {{corners:object, clipped:boolean}|null} null when either rect is
 *        degenerate or a corner is missing — the caller then leaves the marks
 *        alone rather than writing something invented over them.
 *        `clipped` reports that at least one corner fell outside the new crop
 *        and was clamped to the edge: still the closest honest answer, but the
 *        calibration genuinely lost information and the user has to be told.
 */
export function remapCorners(corners, from, to) {
  if (!corners || !usable(from) || !usable(to)) return null;
  const fw = from.x1 - from.x0, fh = from.y1 - from.y0;
  const tw = to.x1 - to.x0, th = to.y1 - to.y0;

  const out = {};
  let clipped = false;
  for (const k of KEYS) {
    const c = corners[k];
    if (!c || !isFinite(c.x) || !isFinite(c.y)) return null;
    const x = (from.x0 + c.x * fw - to.x0) / tw;
    const y = (from.y0 + c.y * fh - to.y0) / th;
    if (x < 0 || x > 1 || y < 0 || y > 1) clipped = true;
    out[k] = { x: clamp(x, 0, 1), y: clamp(y, 0, 1) };
  }
  return { corners: out, clipped };
}
